/**
 * Feature Flags Management System
 *
 * Supports both localStorage persistence and URL parameter controls
 * for enabling/disabling feature flags on both desktop and mobile devices.
 *
 * ## Setup:
 * 1. Add your feature flags to AVAILABLE_FEATURE_FLAGS array
 * 2. Call initFeatureFlags() in your app initialization (client-side only)
 * 3. Use isFeatureFlagEnabled('flagName') to check flag status
 *
 * ## Usage Examples:
 *
 * ### In Components:
 * ```tsx
 * if (isFeatureFlagEnabled('greystar')) {
 *   return <NewFeature />;
 * }
 * ```
 *
 * ### Via Console (Desktop):
 * ```javascript
 * featureFlags.enable('greystar')    // Enable a flag
 * featureFlags.disable('greystar')   // Disable a flag
 * featureFlags.list()                // Show enabled flags
 * featureFlags.listAvailable()       // Show all available flags
 * featureFlags.clear()               // Clear all flags
 * featureFlags.help()                // Show help
 * ```
 *
 * ### Via URL (Mobile-friendly):
 * - Enable: `?enableFeatureFlag=greystar` or `?enableFeatureFlag=flag1,flag2`
 * - Disable: `?disableFeatureFlag=greystar`
 * - Clear all: `?clearFeatureFlags=true`
 *
 * ### React Hook (Recommended):
 * ```tsx
 * const useFeatureFlag = (flagName: string) => {
 *   const [enabled, setEnabled] = useState(false);
 *   useEffect(() => {
 *     setEnabled(isFeatureFlagEnabled(flagName));
 *   }, [flagName]);
 *   return enabled;
 * };
 *
 * // Usage
 * const showNewUI = useFeatureFlag('newSearchUI');
 * ```
 *
 * ## Important Notes:
 * - Page reload required after enabling/disabling flags
 * - Flags persist in localStorage (survives page refreshes)
 * - URL parameters are cleaned up after processing
 * - Only flags in AVAILABLE_FEATURE_FLAGS can be enabled
 */

// Define all available feature flags in your system
export const AVAILABLE_FEATURE_FLAGS: ReadonlyArray<string> = [
  // Add new feature flags here as you develop them
  'mockInquiries', // Selectively disable sending contact inquiries to the backend API
  'hotpads-next', // Enroll in the HotPads Next (beta) experience; sets the `hp_next` routing cookie
];

// Type derived from the available flags
export type FeatureFlagName = (typeof AVAILABLE_FEATURE_FLAGS)[number];

// Flags that persist beyond localStorage — they set a cookie (for SSR on future
// page loads) and, optionally, update Redux (for client-side transitions on the
// current load). Key = normalized (lowercase) flag name.
//
// IMPORTANT: When `reduxKey` is provided it must correspond to an existing key
// in the AppState type (see flux/reducers/app.ts) for TypeScript + Redux to work
// correctly, and the server must read the cookie in populateStore.js to hydrate
// the flag into Redux during SSR. Omit `reduxKey` for cookie-only flags whose
// effect lives outside Redux (e.g. the `hp_next` cookie, read by edge routing).
export type CookieBackedFlag = {
  cookie: string;
  reduxKey?: string;
  persistLocalStorage?: boolean;
};

// HAProxy routing cookie set when a user enrolls in HotPads Next. Mirrors
// apps/hotpads-web-next/src/middleware/rolloutCookies.ts (HP_NEXT_COOKIE).
const HP_NEXT_COOKIE = 'hp_next';

export const COOKIE_BACKED_FLAGS: Readonly<Record<string, CookieBackedFlag>> = {
  // Enrolling in HotPads Next sets the `hp_next` cookie that edge routing reads to
  // serve the Next.js app. Cookie-only — no localStorage or Redux state backs it.
  'hotpads-next': { cookie: HP_NEXT_COOKIE, persistLocalStorage: false },
};

const STORAGE_KEY = 'featureFlags';
const ACTIVE_FLAGS_KEY = 'activeFeatureFlags';

// Mirrors apps/hotpads-web-next/src/middleware/rolloutCookies.ts. Keep this
// aligned with that cookie contract: manual beta enables clear rollout state,
// and manual beta clears write betaRolloutOptOut.
const BETA_ROLLOUT_EXPERIMENT = 'HOTPADS_WEB_NEXT_REWRITE';
const BETA_ROLLOUT_MARKER_COOKIE = 'betaRollout';
const BETA_ROLLOUT_CHECK_COOKIE = 'betaRolloutCheckedAt';
const BETA_ROLLOUT_OPT_OUT_COOKIE = 'betaRolloutOptOut';

const clearCookie = (name: string): void => {
  document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
};

const clearRolloutCookies = (): void => {
  clearCookie(BETA_ROLLOUT_MARKER_COOKIE);
  clearCookie(BETA_ROLLOUT_CHECK_COOKIE);
};

const clearRolloutOptOut = (): void => {
  clearCookie(BETA_ROLLOUT_OPT_OUT_COOKIE);
};

const setRolloutOptOut = (): void => {
  document.cookie = `${BETA_ROLLOUT_OPT_OUT_COOKIE}=${BETA_ROLLOUT_EXPERIMENT}; path=/; max-age=31536000; SameSite=Lax`;
};

const shouldPersistFlagInLocalStorage = (flagName: string): boolean =>
  COOKIE_BACKED_FLAGS[flagName.toLowerCase()]?.persistLocalStorage !== false;

const syncFlagOn = (flag: CookieBackedFlag): void => {
  // None+Secure (not Lax): `hp_next` routing cookie must survive OAuth's cross-site form_post callback.
  document.cookie = `${flag.cookie}=true; path=/; max-age=31536000; SameSite=None; Secure`;
  if (flag.cookie === HP_NEXT_COOKIE) {
    clearRolloutCookies();
    clearRolloutOptOut();
  }
  if (flag.reduxKey && window.store) {
    window.store.dispatch({
      type: 'SET_APP_STORE_BOOL',
      payload: { name: flag.reduxKey, bool: true },
    });
  }
};

const syncFlagOff = (flag: CookieBackedFlag): void => {
  clearCookie(flag.cookie);
  if (flag.cookie === HP_NEXT_COOKIE) {
    // A rollout-enrolled user manually opting out: remember the opt-out after
    // URL cleanup reloads, and clear rollout source/stale check state.
    setRolloutOptOut();
    clearRolloutCookies();
  }
  if (flag.reduxKey && window.store) {
    window.store.dispatch({
      type: 'SET_APP_STORE_BOOL',
      payload: { name: flag.reduxKey, bool: false },
    });
  }
};

/**
 * Get the list of currently active feature flags in the system.
 * These are flags that are actually implemented and available.
 */
export const getActiveFeatureFlags = (): Array<string> => {
  if (typeof window === 'undefined') return [];

  // Check if we have a stored list of active flags
  const stored = localStorage.getItem(ACTIVE_FLAGS_KEY);
  if (stored) {
    return stored.split(',').filter(Boolean);
  }

  // Fallback to the hardcoded list
  return [...AVAILABLE_FEATURE_FLAGS];
};

/**
 * Update the list of active feature flags.
 * This would typically be called by your deployment or configuration system.
 */
export const updateActiveFeatureFlags = (flags: Array<string>): void => {
  if (typeof window === 'undefined') return;

  // Normalize AVAILABLE_FEATURE_FLAGS for comparison
  const availableFlagsLower = AVAILABLE_FEATURE_FLAGS.map((f) => f.toLowerCase());

  const validFlags = flags.filter((flag) => availableFlagsLower.includes(flag.toLowerCase()));

  localStorage.setItem(ACTIVE_FLAGS_KEY, validFlags.join(','));

  // Clean up any enabled flags that are no longer active
  cleanupInactiveFlags();

  // eslint-disable-next-line no-console
  console.log(
    '%c✓ Active feature flags updated',
    'background: #4CAF50; color: white; padding: 2px 6px; border-radius: 2px;',
  );
};

/**
 * Remove any enabled flags that should no longer be persisted in localStorage.
 */
const cleanupInactiveFlags = (): void => {
  const activeFlags = getActiveFeatureFlags();
  const activeFlagsLower = activeFlags.map((f) => f.toLowerCase());
  const enabledFlags = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');

  const validEnabledFlags = enabledFlags.filter(
    (flag: string) => activeFlagsLower.includes(flag.toLowerCase()) && shouldPersistFlagInLocalStorage(flag),
  );

  if (validEnabledFlags.length !== enabledFlags.length) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(validEnabledFlags));

    const removedFlags = enabledFlags.filter(
      (flag: string) => !activeFlagsLower.includes(flag.toLowerCase()) || !shouldPersistFlagInLocalStorage(flag),
    );

    // eslint-disable-next-line no-console
    console.log(
      '%c🧹 Cleaned up inactive flags:%c ' + removedFlags.join(', '),
      'background: #FF9800; color: white; padding: 2px 6px; border-radius: 2px;',
      'color: #FF9800;',
    );
  }
};

/**
 * Process URL parameters to enable/disable/clear feature flags.
 * Supports:
 * - ?enableFeatureFlag=flagName (or comma-separated: flag1,flag2)
 * - ?disableFeatureFlag=flagName (or comma-separated: flag1,flag2)
 * - ?clearFeatureFlags=true
 *
 * Automatically cleans up URL after processing.
 */
const isCookieBackedFlag = (flagName: string): boolean => Boolean(COOKIE_BACKED_FLAGS[flagName.toLowerCase()]);

export const processFeatureFlagsFromURL = (): void => {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  let shouldCleanUrl = false;
  // Cookie-backed flags (e.g. the `hp_next` routing cookie) are read server-side, so
  // toggling one via URL only takes effect after a fresh request. Reload once the
  // cookie is written and the URL is cleaned.
  let shouldReload = false;

  const shouldClearFeatureFlags = params.get('clearFeatureFlags') === 'true';

  // Handle clearing all flags. Clear wins over enable/disable params.
  if (shouldClearFeatureFlags) {
    clearFeatureFlags();
    shouldCleanUrl = true;
    if (Object.keys(COOKIE_BACKED_FLAGS).length > 0) {
      shouldReload = true;
    }
  }

  // Handle enabling flags (comma-separated support)
  const enableFlags = params.get('enableFeatureFlag')?.split(',') || [];
  if (!shouldClearFeatureFlags) {
    enableFlags.forEach((flag) => {
      const flagName = flag?.trim();
      if (flagName) {
        enableFeatureFlag(flagName);
        shouldCleanUrl = true;
        if (isCookieBackedFlag(flagName)) {
          shouldReload = true;
        }
      }
    });
  }

  // Handle disabling flags (comma-separated support)
  const disableFlags = params.get('disableFeatureFlag')?.split(',') || [];
  if (!shouldClearFeatureFlags) {
    disableFlags.forEach((flag) => {
      const flagName = flag?.trim();
      if (flagName) {
        disableFeatureFlag(flagName);
        shouldCleanUrl = true;
        if (isCookieBackedFlag(flagName)) {
          shouldReload = true;
        }
      }
    });
  }

  // Clean up URL after processing flag parameters
  if (shouldCleanUrl) {
    params.delete('enableFeatureFlag');
    params.delete('disableFeatureFlag');
    params.delete('clearFeatureFlags');

    const newUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;

    window.history.replaceState({}, '', newUrl);
  }

  // Reload onto the cleaned URL so edge routing picks up the cookie change.
  if (shouldReload) {
    window.location.reload();
  }
};

/**
 * Clears all feature flags by removing the featureFlags key from localStorage.
 */
export const clearFeatureFlags = (): void => {
  if (typeof window === 'undefined') return;

  localStorage.removeItem(STORAGE_KEY);

  Object.values(COOKIE_BACKED_FLAGS).forEach((flag) => {
    syncFlagOff(flag);
  });

  // eslint-disable-next-line no-console
  console.log(
    '%c✓ Feature flags cleared%c Please reload the page',
    'background: #4CAF50; color: white; padding: 2px 6px; border-radius: 2px;',
    'color: #4CAF50;',
  );
};

/**
 * Enables a feature flag by adding it to localStorage.
 */
export const enableFeatureFlag = (flagName: string): void => {
  if (typeof window === 'undefined') return;

  const normalizedFlag = flagName.toLowerCase();
  const activeFlags = getActiveFeatureFlags();
  const activeFlagsLower = activeFlags.map((f) => f.toLowerCase());

  // Check if flag is in active flags list (case-insensitive)
  if (!activeFlagsLower.includes(normalizedFlag)) {
    // eslint-disable-next-line no-console
    console.log(
      `%c⚠ Feature flag "${flagName}" is not in the active flags list%c\nAvailable flags: ${activeFlags.join(', ')}`,
      'background: #FF9800; color: white; padding: 2px 6px; border-radius: 2px;',
      'color: #FF9800;',
    );
    return;
  }

  const currentFlags = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  const backedFlag = COOKIE_BACKED_FLAGS[normalizedFlag];
  const shouldPersist = shouldPersistFlagInLocalStorage(normalizedFlag);

  if (!shouldPersist) {
    const storedFlagsWithoutCookieOnlyFlag = currentFlags.filter((flag: string) => flag !== normalizedFlag);
    if (storedFlagsWithoutCookieOnlyFlag.length !== currentFlags.length) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(storedFlagsWithoutCookieOnlyFlag));
    }

    if (backedFlag) {
      syncFlagOn(backedFlag);
    }

    // eslint-disable-next-line no-console
    console.log(
      `%c✓ Feature flag "${flagName}" enabled%c Please reload the page`,
      'background: #4CAF50; color: white; padding: 2px 6px; border-radius: 2px;',
      'color: #4CAF50;',
    );
    return;
  }

  if (!currentFlags.includes(normalizedFlag)) {
    currentFlags.push(normalizedFlag);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(currentFlags));

    if (backedFlag) {
      syncFlagOn(backedFlag);
    }

    // eslint-disable-next-line no-console
    console.log(
      `%c✓ Feature flag "${flagName}" enabled%c Please reload the page`,
      'background: #4CAF50; color: white; padding: 2px 6px; border-radius: 2px;',
      'color: #4CAF50;',
    );
  } else {
    // eslint-disable-next-line no-console
    console.log(`%cℹ Feature flag "${flagName}" is already enabled`, 'color: #2196F3;');
  }
};

/**
 * Disables a feature flag by removing it from localStorage.
 */
export const disableFeatureFlag = (flagName: string): void => {
  if (typeof window === 'undefined') return;

  const currentFlags = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  const normalizedFlag = flagName.toLowerCase();
  const updatedFlags = currentFlags.filter((flag: string) => flag !== normalizedFlag);
  const wasInLocalStorage = currentFlags.length !== updatedFlags.length;

  if (wasInLocalStorage) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedFlags));
  }

  // Always sync cookie-backed flags off, even if the flag wasn't in localStorage
  // (e.g., enabled via env var or cookie set directly)
  const backedFlag = COOKIE_BACKED_FLAGS[normalizedFlag];
  if (backedFlag) {
    syncFlagOff(backedFlag);
  }

  if (wasInLocalStorage || backedFlag) {
    // eslint-disable-next-line no-console
    console.log(
      `%c✗ Feature flag "${flagName}" disabled%c Please reload the page`,
      'background: #f44336; color: white; padding: 2px 6px; border-radius: 2px;',
      'color: #f44336;',
    );
  } else {
    // eslint-disable-next-line no-console
    console.log(`%cℹ Feature flag "${flagName}" was not enabled`, 'color: #2196F3;');
  }
};

/**
 * Checks if a feature flag is enabled.
 * Only checks localStorage flags.
 */
export const isFeatureFlagEnabled = (flagName: string): boolean => {
  if (typeof window === 'undefined') return false;

  const normalizedFlag = flagName.toLowerCase();

  // First check if flag is in active flags list (case-insensitive)
  const activeFlags = getActiveFeatureFlags();
  const activeFlagsLower = activeFlags.map((f) => f.toLowerCase());
  if (!activeFlagsLower.includes(normalizedFlag)) {
    return false;
  }

  // Check localStorage flags
  const currentFlags = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  return shouldPersistFlagInLocalStorage(normalizedFlag) && currentFlags.includes(normalizedFlag);
};

/**
 * Returns an array of all currently enabled feature flags from localStorage.
 */
export const getEnabledFeatureFlags = (): Array<string> => {
  if (typeof window === 'undefined') return [];

  const enabledFlags = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  const activeFlags = getActiveFeatureFlags();
  const activeFlagsLower = activeFlags.map((f) => f.toLowerCase());

  // Only return flags that are both enabled AND active (case-insensitive)
  return enabledFlags.filter(
    (flag: string) => activeFlagsLower.includes(flag.toLowerCase()) && shouldPersistFlagInLocalStorage(flag),
  );
};

/**
 * Get all enabled flags from localStorage
 */
export const getAllEnabledFlags = (): Array<string> => {
  return getEnabledFeatureFlags();
};

/**
 * Initialize feature flags system
 */
export const initFeatureFlags = (): void => {
  if (typeof window === 'undefined') return;

  // Clean up any inactive flags first
  cleanupInactiveFlags();

  // Process URL parameters
  processFeatureFlagsFromURL();

  // Sync enabled cookie-backed flags to cookies + Redux (handles migration
  // and first-visit via URL param where SSR didn't have the cookie yet).
  const enabledFlagsForSync = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  Object.entries(COOKIE_BACKED_FLAGS).forEach(([flagName, flag]) => {
    if (enabledFlagsForSync.includes(flagName)) {
      syncFlagOn(flag);
    }
  });

  // Initialize feature flags on window object
  window.featureFlags = {
    enable: (name: string) => enableFeatureFlag(name),
    disable: (name: string) => disableFeatureFlag(name),
    clear: () => clearFeatureFlags(),
    isEnabled: (name: string) => isFeatureFlagEnabled(name),
    list: () => getAllEnabledFlags(),
    listStored: () => getEnabledFeatureFlags(),
    listAvailable: () => getActiveFeatureFlags(),
    updateActive: (flags: Array<string>) => updateActiveFeatureFlags(flags),
    help: () => {
      const activeFlags = getActiveFeatureFlags();
      // eslint-disable-next-line no-console
      console.log(
        `
%c🚩 Feature Flags Help%c

%cConsole Commands:%c
  featureFlags.enable('flagName')     Enable a flag
  featureFlags.disable('flagName')    Disable a flag  
  featureFlags.clear()                Clear all flags
  featureFlags.isEnabled('flagName')  Check if flag is enabled
  featureFlags.list()                 Show all enabled flags
  featureFlags.listStored()           Show only localStorage flags
  featureFlags.listAvailable()        Show available flags

%cURL Parameters:%c
  ?enableFeatureFlag=flag1,flag2      Enable flags
  ?disableFeatureFlag=flag1,flag2     Disable flags  
  ?clearFeatureFlags=true             Clear all flags

%cAvailable Flags:%c ${activeFlags.join(', ')}

%cCookie-Backed Flags:%c ${Object.keys(COOKIE_BACKED_FLAGS).join(', ')}
  These flags also set a browser cookie so the server can read them during SSR.

%cNote:%c Page reload required after changes
`,
        'background: #2196F3; color: white; font-weight: bold; padding: 4px 8px; border-radius: 4px;',
        '',
        'color: #2196F3; font-weight: bold;',
        '',
        'color: #2196F3; font-weight: bold;',
        '',
        'color: #4CAF50; font-weight: bold;',
        '',
        'color: #FF9800; font-weight: bold;',
        '',
        'color: #666; font-style: italic;',
        '',
      );
    },
  };

  // Log active flags if any
  const enabledFlags = getAllEnabledFlags();
  if (enabledFlags.length > 0) {
    // eslint-disable-next-line no-console
    console.log(
      '%c🚩 HPWEB - Enabled feature flags:%c ' + enabledFlags.join(', '),
      'background: #2196F3; color: white; font-size: 12px; padding: 2px 6px; border-radius: 2px;',
      'color: #2196F3; font-weight: bold;',
    );

    // eslint-disable-next-line no-console
    console.log(
      '%cℹ Type %cfeatureFlags.help()%c for more info',
      'color: #666; font-size: 11px;',
      'background: #f5f5f5; padding: 1px 4px; border-radius: 2px; font-family: monospace;',
      'color: #666; font-size: 11px;',
    );
  }
};

// Type declarations for window
declare global {
  interface Window {
    featureFlags: {
      enable: (name: string) => void;
      disable: (name: string) => void;
      clear: () => void;
      isEnabled: (name: string) => boolean;
      list: () => Array<string>;
      listStored: () => Array<string>;
      listAvailable: () => Array<string>;
      updateActive: (flags: Array<string>) => void;
      help: () => void;
    };
  }
}
